import { ReactNode } from 'react'

import type {
  NavMenuContentType,
  Props as NavigationV2Props,
} from '@hhgtech/hhg-components/navigation'

import ErrorBoundary from 'components/templates/navigatorLayout/errorBoundary'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS } from 'interfaces/constants'
import type {
  ContentSponsor,
  FooterInfo,
  SiteWideBanner,
} from 'interfaces/types'

import { BelowMainContent } from './belowMainContent'
import { DiscoverHead, SEOData as SEODataType } from './discoverHead'
import { MainNav } from './mainNav'
import { NavFooter } from './navFooter'

export type Props = {
  title?: string
  description?: string
  image?: string
  children?: ReactNode
  onTopBarAppears?: (b: boolean) => void
  className?: string
  SEOData?: SEODataType
  type?: 'article' | 'website' | 'profile'
  isSponsored?: boolean
  sponsoredType?: 'article' | 'content'
  sponsoredBy?: ContentSponsor | ContentSponsor[]
  sitewideBanner?: SiteWideBanner | null
  footerInfo?: FooterInfo
  languageOptions?: NavigationV2Props['languageOptions']
  noUsingRouterPush?: boolean
  dataNavMenuContent?: NavMenuContentType
  activeMenuService?: NavigationV2Props['activeMenuService']
  hideWelcomePopup?: boolean
  subHead?: JSX.Element
  isHealthToolResultSharing?: boolean
  isHomepage?: boolean
  isInline?: boolean
}

const LanguageTextLocaleMap: {
  [k: string]: string
} = {
  English: 'en',
  Filipino: 'tl',
}
const LanguageTextLabelMap: {
  [k: string]: string
} = {
  English: 'EN',
  Filipino: 'FIL',
}

export const getDefaultLanguageOptions = (locale: string, asPath: string) =>
  ['en-PH', 'tl-PH'].includes(locale)
    ? [
        {
          label: 'EN',
          value: asPath,
          locale: 'en-PH',
        },
        {
          label: 'FIL',
          value: '/fil' + asPath,
          locale: 'tl-PH',
        },
      ]
    : []

const NavigatorLayout = ({
  title,
  description,
  image,
  children,
  onTopBarAppears,
  SEOData = {},
  type = 'website',
  footerInfo,
  sitewideBanner,
  isSponsored,
  sponsoredType,
  sponsoredBy,
  noUsingRouterPush,
  dataNavMenuContent,
  languageOptions: languageOptionsProp,
  activeMenuService,
  subHead,
  isHealthToolResultSharing,
  isHomepage = false,
  isInline,
}: // hideWelcomePopup,
Props) => {
  const { locale } = useRouterExtend()
  const siteName = LOCALE_SPECS[locale].SITE_NAME_FORMATTED
  const languageOptions = languageOptionsProp?.map((l) => ({
    ...l,
    locale: l.locale
      ? l.locale
      : LanguageTextLocaleMap[l.label] + '-' + locale.split('-')[1],
    label: LanguageTextLabelMap[l.label] || l.label,
    originalLabel: l.label,
  }))

  return (
    <>
      <ErrorBoundary>
        <DiscoverHead
          SEOData={{ title, image, description, siteName, type, ...SEOData }}
          noUsingRouterPush={noUsingRouterPush}
          languageOptions={languageOptions?.map((l) => ({
            ...l,
            label: l.originalLabel,
          }))}
          isHealthToolResultSharing={isHealthToolResultSharing}
        />
      </ErrorBoundary>
      {isInline ? null : (
        <MainNav
          onTopBarAppears={onTopBarAppears}
          isSponsored={isSponsored}
          sponsoredType={sponsoredType}
          sponsoredBy={sponsoredBy}
          sitewideBanner={sitewideBanner}
          languageOptions={languageOptions}
          dataNavMenuContent={dataNavMenuContent}
          activeMenuService={activeMenuService}
          subHead={subHead}
          isHomepage={isHomepage}
        />
      )}
      <ErrorBoundary fallback={<div style={{ width: '100%', height: 200 }} />}>
        {children}
      </ErrorBoundary>
      {isInline ? null : (
        <>
          <BelowMainContent />
          <NavFooter footerInfo={footerInfo} />
        </>
      )}
    </>
  )
}

export { NavigatorLayout }
